import React, { useState } from "react";
import {
  classifyConjunction,
  contactScore,
  irradianceAtAU,
  lightTimeSeconds,
  round,
  solarFluxFactor,
  synodicPeriodDays,
  thermalEquilibriumTemperature,
} from "../science/kernel.mjs";
import { OrbitBoard, TimelineChart } from "../components/VisualCharts.jsx";
import { DataTable, EvidencePanel, Metric, PageHeader } from "../components/ui.jsx";

export function SolarClock() {
  const [rAu, setRAu] = useState(0.3871);
  const [distanceKm, setDistanceKm] = useState(120_000_000);
  const [elongationDeg, setElongationDeg] = useState(18);
  const flux = solarFluxFactor(rAu);
  const irradiance = irradianceAtAU(rAu);
  const thermal = thermalEquilibriumTemperature({
    irradianceWm2: irradiance,
    albedo: 0.12,
    emissivity: 0.9,
    mode: "subsolar",
  });
  const thermalAvg = thermalEquilibriumTemperature({
    irradianceWm2: irradiance / 4,
    albedo: 0.12,
    emissivity: 0.9,
    mode: "spherical",
  });
  const lightMinutes = lightTimeSeconds(distanceKm) / 60;
  const conjunction = classifyConjunction({
    elongationDeg,
    observerTargetDistanceAu: distanceKm / 149_597_870.7,
    observerSunDistanceAu: 1,
  });
  const synodic = synodicPeriodDays(87.9691, 365.25636);
  const contact = contactScore({
    clearWindowFraction: conjunction === "clear" ? 0.78 : 0.25,
    solarInterferencePenalty: conjunction === "clear" ? 0.12 : 0.7,
    relayAvailability: 0.45,
    latencyMinutes: lightMinutes,
    dataRateClass: conjunction === "clear" ? "medium" : "low",
  });

  const events = [
    ["Earth–Mercury direct window", "Window", "07:00", "Medium"],
    ["Earth-Mercury-Earth relay", "Window", "12:00", "High"],
    ["Autonomy-only operations", "Blackout", "19:00", "Critical"],
  ];
  const timelineRows = [
    {
      label: "Earth-Mercury direct",
      lanes: ["clear", "degraded", "blackout", "clear"],
    },
    {
      label: "Earth-Venus-Mercury relay",
      lanes: ["clear", "clear", "degraded", "clear"],
    },
  ];

  return (
    <section className="module">
      <PageHeader
        title="SOLAR CLOCK"
        muted="TDB metadata + windows"
        subtitle="Deterministic kernels first; SPICE/Orbit windows will plug in next."
      />
      <div className="metric-strip">
        <Metric label="Heliocentric time" value="TDB key" />
        <Metric label="Mercury cycle" value={`${round(0.22 * 100, 2)}%`} />
        <Metric label="Mercury cycle compatibility" value="Verified for horizon slice" />
        <Metric label="Kernel coverage" value={`${round(2026 / 2, 0)}`} />
        <Metric label="Data source status" value="Snapshot only" />
      </div>

      <div className="clock-grid">
        <EvidencePanel
          title="Orbit geometry"
          status={conjunction}
          tone={conjunction === "clear" ? "green" : "red"}
          className="panel-large"
        >
          <OrbitBoard />
        </EvidencePanel>

        <EvidencePanel title="Geometry and relay" status="computed" tone="blue">
          <label className="inline-label">
            Mercury radius (AU)
            <input type="number" step="0.0001" value={rAu} onChange={(event) => setRAu(Number(event.target.value))} />
          </label>
          <label className="inline-label">
            Earth-Mercury distance (km)
            <input
              type="number"
              step="1000000"
              value={distanceKm}
              onChange={(event) => setDistanceKm(Number(event.target.value))}
            />
          </label>
          <label className="inline-label">
            Solar elongation (deg)
            <input
              type="number"
              step="1"
              value={elongationDeg}
              onChange={(event) => setElongationDeg(Number(event.target.value))}
            />
          </label>
          <div className="metric-grid-1">
            <Metric label="Solar flux factor" value={`${round(flux, 2)}x`} />
            <Metric label="Irradiance" value={`${round(irradiance, 0)} W/m²`} />
            <Metric label="Subsolar temperature" value={`${round(thermal, 1)} K`} />
            <Metric label="Spherical estimate" value={`${round(thermalAvg, 1)} K`} />
            <Metric label="One-way light time" value={`${round(lightMinutes, 2)} min`} />
            <Metric label="Round trip" value={`${round(lightMinutes * 2, 2)} min`} />
            <Metric label="Synodic period" value={`${round(synodic, 1)} days`} />
            <Metric label="Contact score" value={round(contact, 1)} />
          </div>
        </EvidencePanel>
      </div>

      <EvidencePanel title="Contact windows and next events" status="roadmap">
        <TimelineChart rows={timelineRows} />
        <DataTable
          shell="event"
          columns={[
            { key: "event", header: "Event", render: ([name]) => name },
            { key: "type", header: "Type", render: ([, type]) => type },
            { key: "window", header: "Window", render: ([, , window]) => window },
            { key: "priority", header: "Priority", render: ([, , , priority]) => priority },
          ]}
          rows={events}
          getRowKey={([name]) => name}
        />
      </EvidencePanel>
    </section>
  );
}
